import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  csrfTokenFromFlow,
  fetchLoginFlow,
  flowHasNode,
  flowMessages,
  loginFlowURL,
  submitFlow,
  whoami,
  type Flow,
} from '../../lib/kratos';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Logo } from '../../components/Logo';

export function LoginPage() {
  const [params] = useSearchParams();
  const flowId = params.get('flow');
  const [flow, setFlow] = useState<Flow | null>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // If Kratos already has an active session, don't bounce back into the
      // login flow — go straight to the dashboard. This breaks the / ↔ /login
      // loop that happens when the backend can't validate the cookie.
      const session = await whoami();
      if (cancelled) return;
      if (session?.active) {
        navigate('/', { replace: true });
        return;
      }
      if (!flowId) {
        window.location.replace(loginFlowURL());
        return;
      }
      try {
        const f = await fetchLoginFlow(flowId);
        if (!cancelled) setFlow(f);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'flow load failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flowId, navigate]);

  if (!flow) {
    return <div className="p-10 text-center text-sm text-ink-tertiary">Loading…</div>;
  }

  const codeStage = flowHasNode(flow, 'code');
  const csrf = csrfTokenFromFlow(flow);
  const messages = flowMessages(flow);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        method: 'code',
        csrf_token: csrf,
        identifier: email,
      };
      if (codeStage) {
        body.code = code;
      }
      const updated = await submitFlow<Flow & { session?: unknown }>(flow!.ui.action, body);
      // After successful code verification Kratos returns a session payload.
      if (updated && 'session' in updated && updated.session) {
        await qc.invalidateQueries({ queryKey: ['me'] });
        navigate('/', { replace: true });
        return;
      }
      // Otherwise we just got the next stage of the flow.
      if (updated && 'ui' in updated) {
        setFlow(updated as Flow);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'login failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-soft px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Logo variant="lockup" size={32} />
        </div>
        <Card>
          <CardHeader title="Sign in to Sigil" description="We'll email you a code to confirm." />
          <CardBody>
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Email"
                type="email"
                name="identifier"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                disabled={codeStage || busy}
              />
              {codeStage && (
                <Input
                  label="One-time code"
                  type="text"
                  name="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  required
                  hint="Check your inbox for a 6-digit code."
                />
              )}
              {messages.map((m) => (
                <p key={m.id} className={m.type === 'error' ? 'text-sm text-bad' : 'text-sm text-ink-secondary'}>
                  {m.text}
                </p>
              ))}
              {error && <p className="text-sm text-bad">{error}</p>}
              <Button type="submit" className="w-full justify-center" disabled={busy}>
                {busy ? 'Working…' : codeStage ? 'Verify code' : 'Send code'}
              </Button>
            </form>
          </CardBody>
        </Card>
        <p className="mt-6 text-center text-sm text-ink-secondary">
          New to Sigil?{' '}
          <Link to="/register" className="font-medium text-ink-primary underline">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
